import { useEffect, useState, useRef } from 'react'
import { TextField, Box, Button } from "@mui/material";
import { NavLink, useParams, useNavigate, useLocation } from "react-router-dom";
import { KeyboardArrowLeft } from '@mui/icons-material';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { usePricePlan } from '../store/PricePlanContext';
import { useAffiliateCode } from '../hooks/useAffiliateCode';

// Map URL term to plan display name (used when user lands on help-me without selecting plan on PricePlan, e.g. from coupon wizard)
const getPlanNameFromTerm = (term) => {
  if (!term) return '';
  const map = {
    'special': 'Special Occasions',
    'short-term': 'Short Term',
    'mid-term': 'Mid Term',
    'long-term': 'Long Term'
  };
  return map[term] || term;
};

const HelpMe = () => {
  const params = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { selectedPlan } = usePricePlan()

  // affiliate code hook
  const { affiliateCode, affiliateCodeValid, affiliateCodeLocked, affiliateCodeError, handleAffiliateCodeChange, isVerifying } = useAffiliateCode();

  const [fullName, setFullName] = useState("")
  const [phone, setPhone] = useState("")
  const [companyName, setCompanyName] = useState("")
  const [email, setEmail] = useState("")
  const [website, setWebsite] = useState("")
  const [couponDesc, setCouponDesc] = useState("")
  const [city, setCity] = useState("")
  const [country, setCountry] = useState("")
  const [term, setTerm] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  
  // Phone input states
  const [phoneValue, setPhoneValue] = useState('')
  const [userCountry, setUserCountry] = useState('us')
  const [isDetectingCountry, setIsDetectingCountry] = useState(true)
  const [isPhoneFocused, setIsPhoneFocused] = useState(false)
  const phoneInputRef = useRef(null)
  
  // Validation states
  const [fullNameNotValid, setFullNameNotValid] = useState(false)
  const [phoneNotValid, setPhoneNotValid] = useState(false)
  const [companyNameNotValid, setCompanyNameNotValid] = useState(false)
  const [emailNotValid, setEmailNotValid] = useState(false)
  const [websiteNotValid, setWebsiteNotValid] = useState(false)
  const [cityNotValid, setCityNotValid] = useState(false)
  const [countryNotValid, setCountryNotValid] = useState(false)
  const [couponDescNotValid, setCouponDescNotValid] = useState(false)
  const [disableSubmitBtn, setDisableSubmitBtn] = useState(true)

  useEffect(() => {
    setTerm(params.term)
    detectUserCountry();
    
    const fallbackTimer = setTimeout(() => {
      if (isDetectingCountry) {
        setIsDetectingCountry(false);
      }
    }, 3000);

    return () => clearTimeout(fallbackTimer);
  }, [params])

  // Add focus/blur event listeners to phone input
  useEffect(() => {
    const phoneInputElement = phoneInputRef.current?.querySelector('input');
    if (phoneInputElement) {
      const handleFocus = () => setIsPhoneFocused(true);
      const handleBlur = () => setIsPhoneFocused(false);
      
      phoneInputElement.addEventListener('focus', handleFocus);
      phoneInputElement.addEventListener('blur', handleBlur);
      
      return () => {
        phoneInputElement.removeEventListener('focus', handleFocus);
        phoneInputElement.removeEventListener('blur', handleBlur);
      };
    }
  }, [phoneValue, userCountry]);

  const detectUserCountry = async () => {
    try {
      setIsDetectingCountry(true);
      const response = await fetch('https://api.country.is/');
      const data = await response.json();
      
      if (data.country) {
        // Convert country code to lowercase for PhoneInput compatibility
        const countryCode = data.country.toLowerCase();
        setUserCountry(countryCode);
      }
    } catch (error) {
      // Fallback to US if detection fails
      setUserCountry('us');
    } finally {
      setIsDetectingCountry(false);
    }
  };

  const handlePhoneChange = (value, country) => {
    // Extract the country code from the current value
    const currentCountryCode = country.dialCode;
    
    // If the value starts with the country code, allow it
    // If not, prepend the country code
    let finalValue = value;
    if (!value.startsWith(currentCountryCode)) {
      finalValue = currentCountryCode + value;
    }
    
    setPhoneValue(finalValue);
    setPhone(finalValue);

    // Validate phone number - same as CompanyDetails: at least 7 characters
    if (finalValue && finalValue.length >= 7) {
      setPhoneNotValid(false);
    } else {
      setPhoneNotValid(true);
    }
  }

  const handleCountryChange = (country) => {
    setUserCountry(country.iso2);
    
    // If there's an existing phone number, update it with the new country code
    if (phoneValue && phoneValue.length > 0) {
      // Extract the phone number without country code (remove leading digits)
      const phoneWithoutCountry = phoneValue.replace(/^\d+/, '');
      const newPhoneValue = country.dialCode + phoneWithoutCountry;
      setPhoneValue(newPhoneValue);
      setPhone(newPhoneValue);

      if (newPhoneValue.length >= 7) {
        setPhoneNotValid(false);
      } else {
        setPhoneNotValid(true);
      }
    } else {
      setPhoneNotValid(false);
    }
  }

  // Validation logic - only set to valid when fields have content
  useEffect(() => {
    // Only set to valid when fields have content, don't set to invalid when empty
    if (fullName.trim() !== '') {
      setFullNameNotValid(false);
    }

    if (email.trim() !== '' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailNotValid(false);
    }

    if (phone.trim() !== '' && phone.length >= 7) {
      setPhoneNotValid(false);
    }

    if (companyName.trim() !== '') {
      setCompanyNameNotValid(false);
    }

    if (website.trim() !== '') {
      setWebsiteNotValid(false);
    }

    if (city.trim() !== '') {
      setCityNotValid(false);
    }

    if (country.trim() !== '') {
      setCountryNotValid(false);
    }
  }, [fullName, email, phone, companyName, website, city, country])

  // Control submit button state
  useEffect(() => {
    if (
      fullName.trim() !== '' &&
      email.trim() !== '' &&
      phone.trim() !== '' &&
      companyName.trim() !== '' &&
      city.trim() !== '' &&
      country.trim() !== '' &&
      couponDesc.trim() !== '' &&
      !fullNameNotValid &&
      !emailNotValid &&
      !phoneNotValid &&
      !companyNameNotValid &&
      !cityNotValid &&
      !countryNotValid &&
      !couponDescNotValid
    ) {
      setDisableSubmitBtn(false);
    } else {
      setDisableSubmitBtn(true);
    }
  }, [fullName, email, phone, companyName, city, country, couponDesc, fullNameNotValid, emailNotValid, phoneNotValid, companyNameNotValid, cityNotValid, countryNotValid, couponDescNotValid])

  // Change handlers with validation
  const changeFullName = (e) => {
    let value = e.target.value
    setFullName(value)
    // Set validation state based on content
    if (value.trim() !== '') {
      setFullNameNotValid(false);
    } else {
      setFullNameNotValid(true);
    }
  }

  const changePhoneNumber = (e) => {
    let value = e.target.value
    setPhone(value)
    if (value && value.length >= 7) {
      setPhoneNotValid(false);
    } else {
      setPhoneNotValid(true);
    }
  }
  const changeCompanyName = (e) => {
    let value = e.target.value
    setCompanyName(value)
    // Set validation state based on content
    if (value.trim() !== '') {
      setCompanyNameNotValid(false);
    } else {
      setCompanyNameNotValid(true);
    }
  }

  const changeEmail = (e) => {
    let value = e.target.value
    setEmail(value)
    // Set validation state based on content
    if (value.trim() !== '' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setEmailNotValid(false);
    } else if (value.trim() !== '') {
      setEmailNotValid(true);
    }
  }

  const changeWebsite = (e) => {
    let value = e.target.value
    setWebsite(value)
    // Set validation state based on content
    if (value.trim() !== '') {
      setWebsiteNotValid(false);
    }
  }

  const changeCity = (e) => {
    let value = e.target.value
    setCity(value)
    // Set validation state based on content
    if (value.trim() !== '') {
      setCityNotValid(false);
    } else {
      setCityNotValid(true);
    }
  }

  const changeCountry = (e) => {
    let value = e.target.value
    setCountry(value)
    // Set validation state based on content
    if (value.trim() !== '') {
      setCountryNotValid(false);
    } else {
      setCountryNotValid(true);
    }
  }

  const changeCouponDesc = (e) => {
    let value = e.target.value
    setCouponDesc(value)
    setCouponDescNotValid(value.trim() === '')
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation is handled by the button state
    if (disableSubmitBtn) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Prefer plan passed from "Yes" (HelpWithCoupons), then context, then URL term
      const planName = (location.state?.planName && location.state.planName.trim()) ||
        (selectedPlan?.name && selectedPlan.name.trim()) ||
        getPlanNameFromTerm(params.term)
      const payload = {
        plan_name: planName,
        phone,
        company_name: companyName,
        email,
        website: website.trim() || null,
        description: couponDesc,
        city: city.trim(),
        country: country.trim(),
      };

      if (affiliateCodeValid && affiliateCode?.trim()) {
        payload.affiliateCode = affiliateCode.trim().toUpperCase();
      }

      const response = await fetch(`${process.env.REACT_APP_API_URI}/v3/advertising/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to submit lead request');
      }

      const result = await response.json();
      navigate('/advertise/thanks', { state: { name: fullName } });
      
    } catch (error) {
      console.error('Error submitting lead request:', error);
      setSubmitError(error.message || 'There was an error submitting your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container">
      <style>
        {`
          .react-tel-input .country-list .search-box {
            max-width: 163px !important;
          }
        `}
      </style>
      <div className="help-container">
        <div className="help-me">
          <div className="help-heading">
            <h1>We'd love to hear from you</h1>
            <h2>Send us a message and we'll respond as soon as possible</h2>
          </div>
          <div className="help-input">
            <div className="input-first">
              <TextField required label="FULL NAME*" value={fullName} variant="standard" onChange={changeFullName} error={fullNameNotValid} helperText={fullNameNotValid ? "Full name is required" : null}/>

              {/* input for affiliate code */}
              <TextField 
                label="AFFILIATE CODE" 
                value={affiliateCode} 
                variant="standard" 
                onChange={handleAffiliateCodeChange} 
                InputProps={{ readOnly: affiliateCodeLocked }}
                inputProps={affiliateCodeLocked ? { tabIndex: -1 } : {}}
                sx={affiliateCodeLocked ? {
                  '& .MuiInput-underline.Mui-focused:after': { borderBottomColor: 'transparent' },
                  '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: 'rgba(0,0,0,0.42)' },
                  '& .MuiFormLabel-root.Mui-focused': { color: 'rgba(0,0,0,0.6)' },
                  '& input': { cursor: 'default' },
                } : {}}
                error={affiliateCodeError !== null && !affiliateCodeValid} 
                helperText={
                  isVerifying 
                    ? "Verifying..." 
                    : affiliateCodeError && !affiliateCodeValid 
                      ? affiliateCodeError 
                      : affiliateCodeLocked
                        ? "Affiliate code verified and locked"
                      : affiliateCodeValid 
                        ? "Affiliate code verified" 
                        : null
                }
              />

              
            </div>

            <div className="input-second">
              <TextField required label="COMPANY NAME*" value={companyName} variant="standard" onChange={changeCompanyName} error={companyNameNotValid}helperText={companyNameNotValid ? "Company name is required" : null}/>
              
              <Box sx={{ marginTop: "20px", position: 'relative', width: { xs: '300px', sm: '350px' } }} className={isPhoneFocused && !phoneNotValid ? 'phone-input-focused' : ''}>
                <span className={`phone-label ${isPhoneFocused && !phoneNotValid ? 'phone-label-focused' : ''}`} style={{ 
                  position: 'absolute', 
                  top: '-8px', 
                  left: '0', 
                  fontSize: '12px', 
                  color: phoneNotValid 
                    ? '#d32f2f' 
                    : (isPhoneFocused ? '#1976d2' : '#666'), 
                  zIndex: 2, 
                  backgroundColor: 'white', 
                  paddingRight: '4px',
                  transition: 'color 0s ease'
                }}>
                  PHONE NUMBER*
                  {isDetectingCountry && (
                    <span style={{ marginLeft: '8px', fontSize: '10px', color: '#999' }}>
                      (Detecting location...)
                    </span>
                  )}
                </span>
                <div ref={phoneInputRef}>
                  <PhoneInput
                    country={userCountry}
                    value={phoneValue}
                    onChange={handlePhoneChange}
                    onCountryChange={handleCountryChange}
                    containerClass={isPhoneFocused && !phoneNotValid ? 'phone-input-container-focused' : ''}
                    inputStyle={{ 
                      width: '100%', 
                      height: '40px', 
                      border: 'none', 
                      borderBottom: phoneNotValid 
                        ? '2px solid #d32f2f' 
                        : (isPhoneFocused 
                          ? '2px solid #1976d2' 
                          : '1px solid #ccc'), 
                      borderRadius: '0', 
                      fontSize: '16px', 
                      padding: '8px 12px 0px 50px', 
                      backgroundColor: 'transparent', 
                      outline: 'none', 
                      fontFamily: 'inherit',
                      transition: 'border-bottom 0s ease'
                    }}
                    buttonStyle={{ 
                      border: 'none', 
                      backgroundColor: 'transparent', 
                      borderBottom: phoneNotValid 
                        ? '2px solid #d32f2f' 
                        : (isPhoneFocused 
                          ? '2px solid #1976d2' 
                          : '1px solid #ccc'), 
                      borderRadius: '0', 
                      padding: '8px 4px',
                      transition: 'border-bottom 0s ease'
                    }}
                    dropdownStyle={{minWidth: '250px', textAlign: 'left'}}
                    enableSearch={true}
                    searchPlaceholder="Search country..."
                    searchNotFound="No country found"
                    preferredCountries={['us', 'gb', 'in', 'ca', 'au']}
                    disableCountryCode={false}
                    countryCodeEditable={false}
                  />
                </div>
                {phoneNotValid && (
                  <div style={{ 
                    position: 'absolute', 
                    bottom: '-20px', 
                    left: '0', 
                    color: '#d32f2f', 
                    fontSize: '12px', 
                    margin: 0 
                  }}>
                    Please enter a valid phone number.
                  </div>
                )}
              </Box>
            </div>

            <div className="input-second">
              <TextField required label="EMAIL*" value={email} variant="standard" onChange={changeEmail}error={emailNotValid}helperText={emailNotValid ? "Please enter a valid email address" : null}/>
              <TextField label="WEBSITE" value={website} variant="standard" onChange={changeWebsite} error={websiteNotValid} helperText={websiteNotValid ? "Please enter a website" : null}/>
            </div>

            <div className="input-second">
              <TextField required label="VILLAGE, TOWN OR CITY*" value={city} variant="standard" onChange={changeCity} error={cityNotValid} helperText={cityNotValid ? "Village, Town or City is required" : null} />
              <TextField required label="COUNTRY*" value={country} variant="standard" onChange={changeCountry} error={countryNotValid} helperText={countryNotValid ? "Country is required" : null} />
            </div>

            {submitError && (
              <div className="submit-error-box">
                {submitError}
              </div>
            )}

            <div className="help-coupon-desc-wrap" style={{ marginTop: '20px', position: 'relative' }}>
              <textarea
                onChange={changeCouponDesc}
                onBlur={() => setCouponDescNotValid(couponDesc.trim() === '')}
                className={`help-textarea${couponDescNotValid ? ' help-textarea-error' : ''}`}
                style={{
                  resize: 'none',
                  width: '100%'
                }}
                value={couponDesc}
                cols="100"
                rows="5"
                placeholder="Tell us a little what you have in mind for your coupon."
                required
              />
              {couponDescNotValid && (
                <div className="help-coupon-desc-error">
                  Please add a coupon description.
                </div>
              )}
            </div>

            <div className="blue-btn button">
              <button 
                className='btn' 
                onClick={handleSubmit}
                disabled={isSubmitting || disableSubmitBtn}
                style={{
                  opacity: (isSubmitting || disableSubmitBtn) ? 0.6 : 1,
                  cursor: (isSubmitting || disableSubmitBtn) ? 'not-allowed' : 'pointer',
                  backgroundColor: (isSubmitting || disableSubmitBtn) ? '#ccc' : undefined
                }}
              >
                {isSubmitting ? 'SENDING...' : 'SEND'}
              </button>
            </div>
          </div>
        </div>

        <div className="logo-home"></div>
        <div className="back-link-container">
          <NavLink to={`/advertise/${term}/help`} className="back-btn-wizard">
            <Button component="span" style={{ marginBottom: 0 }}>
              <KeyboardArrowLeft /> BACK
            </Button>
          </NavLink>
        </div>
      </div>
    </div>
  )
}

export default HelpMe